/**
 * The one fetch core both apps share. Cross-cutting concerns
 * live here and nowhere else:
 *
 * - Bearer attachment from the in-memory token store; `skipAuth` is a client
 *   property (never a wire header) so auth endpoints never carry a stale token.
 * - Single-flight refresh: concurrent 401s queue behind ONE refresh call,
 *   then replay exactly once each. Refresh failure → hard auth failure.
 * - Envelope normalization is declared PER ENDPOINT (`unwrap`), never
 *   heuristic — the manage plane's shapes differ by sub-plane.
 * - Error normalization to ApiError (stable lowercase `code`).
 * - Idempotency-Key pass-through (generate with `uuid()` at form mount).
 */
import { ApiError, errorFromBody, networkError } from "./errors.js";
import { createTokenStore } from "./tokenStore.js";
export function createClient(options) {
    const tokens = createTokenStore();
    const fetchFn = options.fetchFn ?? fetch;
    let refreshInFlight = null;
    function refreshOnce() {
        // Single-flight: every concurrent 401 awaits the same promise.
        refreshInFlight ??= options
            .refresh()
            .catch(() => null)
            .finally(() => {
            refreshInFlight = null;
        });
        return refreshInFlight;
    }
    function buildUrl(path, query) {
        const url = options.baseUrl.replace(/\/$/, "") + path;
        if (!query)
            return url;
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(query)) {
            if (v !== undefined)
                params.set(k, String(v));
        }
        const qs = params.toString();
        return qs ? `${url}?${qs}` : url;
    }
    async function execute(path, opts, retried) {
        const headers = {};
        if (opts.body !== undefined)
            headers["Content-Type"] = "application/json";
        if (opts.idempotencyKey)
            headers["Idempotency-Key"] = opts.idempotencyKey;
        if (!opts.skipAuth) {
            const token = tokens.get();
            if (token)
                headers["Authorization"] = `Bearer ${token}`;
        }
        let response;
        try {
            response = await fetchFn(buildUrl(path, opts.query), {
                method: opts.method ?? "GET",
                headers,
                body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
                credentials: options.credentials,
                signal: opts.signal,
            });
        }
        catch (cause) {
            throw networkError(cause);
        }
        if (response.status === 401 && !opts.skipAuth && !retried) {
            const newToken = await refreshOnce();
            if (newToken === null) {
                tokens.set(null);
                options.onAuthFailure?.();
                throw new ApiError({ code: "unauthorized", httpStatus: 401 });
            }
            tokens.set(newToken);
            return execute(path, opts, true);
        }
        return response;
    }
    async function request(path, opts = {}) {
        const response = await execute(path, opts, false);
        const requestId = response.headers.get("X-Request-Id") ?? undefined;
        let body = undefined;
        const text = await response.text();
        if (text) {
            try {
                body = JSON.parse(text);
            }
            catch {
                body = undefined;
            }
        }
        if (!response.ok) {
            throw errorFromBody(response.status, body, requestId);
        }
        const unwrap = opts.unwrap ?? "none";
        if (unwrap === "none")
            return body;
        if (unwrap === "okData") {
            const envelope = body;
            if (!envelope || envelope.ok !== true) {
                throw errorFromBody(response.status, body, requestId);
            }
            return envelope.data;
        }
        return body[unwrap.key];
    }
    return { request, tokens };
}
//# sourceMappingURL=client.js.map